import { memo, useEffect, useMemo, useState } from "react";
import { adminAnalytics, adminAnalyticsTimeseries } from "@/lib/apiClient";
import { toast } from "sonner";
import {
  Users, Award, Mic, BookOpen, DollarSign, Activity, Download, CheckCircle2,
  TrendingUp, TrendingDown, Minus, ChevronLeft, ChevronRight, UserPlus,
} from "lucide-react";
import {
  ResponsiveContainer, BarChart, Bar, AreaChart, Area,
  XAxis, YAxis, CartesianGrid, Tooltip, Cell,
} from "recharts";

/**
 * Analytics — period-scoped growth/activity (backed by the new
 * GET /admin/analytics/timeseries) layered on top of the existing lifetime
 * snapshot (GET /admin/analytics, unchanged: module pass-rates, demographic
 * raw user list, CSV exports). Two endpoints, two concerns: one answers
 * "what's true right now", the other "how did that change over a period".
 *
 * Color: every chart here is single-hue (one series) or a 2-6 step ordinal
 * ramp (age bands, attempts->completions funnel) — never a multi-hue
 * categorical set. Both ramps below were validated with the dataviz skill's
 * validator (--ordinal, surface #ffffff): monotone lightness, adjacent
 * ΔL >= 0.06, light-end contrast >= 2:1, single hue. Emerald and gold are
 * the app's own two brand colors, not chart-specific picks.
 */

// Light -> dark, 6 steps, one hue (emerald). Used for the ordinal age-band
// chart (fixed band order carries meaning, so lightness encodes position,
// never sorted by count) and as the module-performance progress-bar fill.
const EMERALD_RAMP = ["#9bb8b1", "#7fa49a", "#628f84", "#457b6d", "#296756", "#064e3b"];
const EMERALD = "#064E3B";
const EMERALD_TRACK = "#E7E0D2"; // existing app border token — meter's unfilled track
const GOLD = "#D97706";
const GOLD_LIGHT = "#e6a75d"; // validated ordinal light step of the gold ramp
const GRID = "#E7E0D2";
const SOFT = "#4B5563";

const PERIODS = [
  { key: "daily", label: "Daily" },
  { key: "weekly", label: "Weekly" },
  { key: "monthly", label: "Monthly" },
  { key: "6m", label: "6M" },
  { key: "yearly", label: "Yearly" },
  { key: "custom", label: "Custom" },
];
const NAVIGABLE = new Set(["daily", "weekly", "monthly", "yearly"]);

const AGE_GROUPS_ORDER = ["Under 18", "18-24", "25-34", "35-44", "45-54", "55+"];

export default function AdminAnalytics() {
  const [data, setData] = useState(null);
  const [period, setPeriod] = useState("weekly");
  const [offset, setOffset] = useState(0);
  const [customStart, setCustomStart] = useState("");
  const [customEnd, setCustomEnd] = useState("");
  const [series, setSeries] = useState(null);
  const [seriesLoading, setSeriesLoading] = useState(false);

  useEffect(() => { adminAnalytics().then(setData).catch(() => toast.error("Could not load analytics")); }, []);

  useEffect(() => {
    if (period === "custom" && (!customStart || !customEnd)) return;
    let cancelled = false;
    setSeriesLoading(true);
    adminAnalyticsTimeseries({
      period, offset,
      ...(period === "custom" ? { start: customStart, end: customEnd } : {}),
    })
      .then((d) => { if (!cancelled) setSeries(d); })
      .catch(() => { if (!cancelled) toast.error("Could not load the selected period"); })
      .finally(() => { if (!cancelled) setSeriesLoading(false); });
    return () => { cancelled = true; };
  }, [period, offset, customStart, customEnd]);

  const changePeriod = (p) => {
    setPeriod(p);
    setOffset(0);
    if (p === "custom" && !customEnd) {
      const today = new Date();
      const start = new Date(today); start.setDate(start.getDate() - 29);
      setCustomStart(start.toISOString().slice(0, 10));
      setCustomEnd(today.toISOString().slice(0, 10));
    }
  };

  if (!data) return <div className="text-[#4B5563]">Loading…</div>;

  return (
    <div data-testid="admin-analytics">
      <h1 className="font-heading text-2xl sm:text-3xl font-semibold text-[#1F2937]">Analytics</h1>

      <PeriodSelector
        period={period} onPeriod={changePeriod}
        offset={offset} onOffset={setOffset}
        customStart={customStart} customEnd={customEnd}
        onCustomStart={setCustomStart} onCustomEnd={setCustomEnd}
        rangeLabel={series?.range?.label}
      />

      {/* Row 1 — lifetime total, then period-scoped KPIs with vs-previous-period deltas */}
      <div className="mt-5 grid sm:grid-cols-2 lg:grid-cols-5 gap-3">
        <Kpi label="Total users" value={data.total_users} icon={<Users className="w-4 h-4 text-[#064E3B]" />} />
        <Kpi label="New users" delta={series?.summary?.new_users} loading={seriesLoading}
          icon={<UserPlus className="w-4 h-4 text-[#064E3B]" />} />
        <Kpi label="Active users" delta={series?.summary?.active_users} loading={seriesLoading}
          icon={<Activity className="w-4 h-4 text-[#10B981]" />} />
        <Kpi label="Learning activity" delta={series?.summary?.module_attempts} loading={seriesLoading}
          sub="module attempts" icon={<BookOpen className="w-4 h-4 text-[#064E3B]" />} />
        <Kpi label="Certificates" delta={series?.summary?.certificates} loading={seriesLoading}
          icon={<Award className="w-4 h-4 text-[#D97706]" />} />
      </div>

      {/* Secondary, lifetime figures — kept from the previous version of this
          page (recitation review queue depth, programme enrollment) since
          they're still genuinely useful, just not part of the requested
          period-scoped Row 1. Deliberately smaller/quieter than the KPI
          cards above so the page doesn't read as two competing headlines. */}
      <div className="mt-3 rounded-xl bg-white border border-[#E7E0D2] px-4 py-2.5 flex flex-wrap items-center gap-x-6 gap-y-1.5 text-sm">
        <StripStat icon={<Mic className="w-3.5 h-3.5 text-[#D97706]" />} label="Pending recitations" value={data.pending_recitations} />
        <StripStat icon={<CheckCircle2 className="w-3.5 h-3.5 text-[#10B981]" />} label="Approved recitations" value={data.approved_recitations} />
        <StripStat icon={<BookOpen className="w-3.5 h-3.5 text-[#064E3B]" />} label="Course enrollments" value={data.course_enrollments} />
        <StripStat icon={<DollarSign className="w-3.5 h-3.5 text-[#D97706]" />} label="Paid enrollments" value={data.paid_program_enrollments} />
      </div>

      {/* Row 2 — User Growth (the headline chart) */}
      <GrowthSection series={series} loading={seriesLoading} />

      {/* Row 3 — Learning / Platform Activity */}
      <ActivitySection series={series} loading={seriesLoading} />

      {/* Row 4 — Module Performance */}
      <ModulePerformance moduleStats={data.module_stats} />

      {/* Row 5 — Demographics */}
      <DemographicBreakdown data={data} />

      {/* Downloadable reports */}
      <ReportsPanel data={data} />

      <div className="mt-6 text-xs text-[#4B5563] bg-white border border-[#E7E0D2] rounded-xl px-4 py-3">
        App configuration (beta mode, peer review, timers) has moved to <span className="font-medium text-[#1F2937]">Modules → App Configuration</span>.
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------
// Period selector — one row, above everything it scopes. Presets first,
// custom range behind its own two date inputs.
// ---------------------------------------------------------------------
function PeriodSelector({ period, onPeriod, offset, onOffset, customStart, customEnd, onCustomStart, onCustomEnd, rangeLabel }) {
  return (
    <div className="mt-4 rounded-2xl bg-white border border-[#E7E0D2] p-3 flex flex-wrap items-center gap-3" data-testid="analytics-period-selector">
      <div className="inline-flex items-center rounded-full bg-[#FDFBF7] border border-[#E7E0D2] p-1 gap-1">
        {PERIODS.map((p) => (
          <button key={p.key} type="button" data-testid={`period-${p.key}`}
            onClick={() => onPeriod(p.key)}
            className={`px-3.5 py-1.5 rounded-full text-sm font-medium transition ${
              period === p.key ? "bg-[#064E3B] text-white" : "text-[#4B5563] hover:text-[#1F2937]"
            }`}>
            {p.label}
          </button>
        ))}
      </div>

      {NAVIGABLE.has(period) && (
        <div className="inline-flex items-center gap-1">
          <button type="button" onClick={() => onOffset(offset - 1)} aria-label="Previous period" data-testid="period-prev"
            className="w-8 h-8 rounded-full grid place-items-center border border-[#E7E0D2] hover:border-[#064E3B] text-[#4B5563] hover:text-[#064E3B] transition">
            <ChevronLeft className="w-4 h-4" />
          </button>
          <button type="button" onClick={() => onOffset(offset + 1)} disabled={offset >= 0} aria-label="Next period" data-testid="period-next"
            className="w-8 h-8 rounded-full grid place-items-center border border-[#E7E0D2] hover:border-[#064E3B] text-[#4B5563] hover:text-[#064E3B] transition disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:border-[#E7E0D2] disabled:hover:text-[#4B5563]">
            <ChevronRight className="w-4 h-4" />
          </button>
          {offset !== 0 && (
            <button type="button" onClick={() => onOffset(0)} className="ml-1 text-xs text-[#064E3B] hover:underline font-medium">
              Jump to current
            </button>
          )}
        </div>
      )}

      {period === "custom" && (
        <div className="inline-flex items-center gap-2">
          <input type="date" value={customStart} onChange={(e) => onCustomStart(e.target.value)}
            data-testid="analytics-custom-start"
            className="rounded-lg bg-[#FDFBF7] border border-[#E7E0D2] focus:border-[#064E3B] px-2.5 py-1.5 text-sm outline-none" />
          <span className="text-[#9CA3AF] text-sm">to</span>
          <input type="date" value={customEnd} onChange={(e) => onCustomEnd(e.target.value)}
            data-testid="analytics-custom-end"
            className="rounded-lg bg-[#FDFBF7] border border-[#E7E0D2] focus:border-[#064E3B] px-2.5 py-1.5 text-sm outline-none" />
        </div>
      )}

      {rangeLabel && (
        <div className="ml-auto text-sm font-medium text-[#1F2937]" data-testid="analytics-range-label">{rangeLabel}</div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------
// KPI card with a vs-previous-period delta pill.
// ---------------------------------------------------------------------
function DeltaPill({ pctChange }) {
  if (pctChange === null || pctChange === undefined) return null;
  const flat = Math.abs(pctChange) < 0.05;
  const up = pctChange > 0;
  const Icon = flat ? Minus : up ? TrendingUp : TrendingDown;
  const color = flat ? "text-[#4B5563] bg-[#F3F4F6]" : up ? "text-[#0ca30c] bg-[#ECFDF5]" : "text-[#DC2626] bg-[#FEF2F2]";
  return (
    <span className={`inline-flex items-center gap-1 text-xs font-semibold px-1.5 py-0.5 rounded-md ${color}`}>
      <Icon className="w-3 h-3" />
      {flat ? "flat" : `${Math.abs(pctChange).toFixed(1)}%`}
    </span>
  );
}

function StripStat({ icon, label, value }) {
  return (
    <span className="inline-flex items-center gap-1.5 text-[#4B5563]">
      {icon}
      <span className="font-semibold text-[#1F2937] tabular-nums">{value ?? 0}</span>
      {label}
    </span>
  );
}

function Kpi({ label, value, delta, sub, icon, loading }) {
  const displayValue = delta ? delta.value : value;
  return (
    <div className="rounded-2xl bg-white border border-[#E7E0D2] p-5">
      <div className="flex items-center justify-between text-xs uppercase tracking-widest text-[#4B5563]">
        <span>{label}</span>{icon}
      </div>
      <div className={`font-heading text-2xl font-semibold mt-2 text-[#1F2937] transition-opacity ${loading ? "opacity-40" : ""}`}>
        {displayValue ?? "—"}
      </div>
      <div className="mt-1.5 flex items-center gap-2 min-h-[20px]">
        {delta && <DeltaPill pctChange={delta.pct_change} />}
        {delta && <span className="text-xs text-[#9CA3AF]">vs previous period</span>}
        {!delta && sub && <span className="text-xs text-[#9CA3AF]">{sub}</span>}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------
// Shared tooltip — values lead (bold, high-contrast), series name follows.
// ---------------------------------------------------------------------
function ChartTooltip({ active, payload, label, rows }) {
  if (!active || !payload || !payload.length) return null;
  return (
    <div className="rounded-xl bg-white border border-[#E7E0D2] shadow-[0_12px_32px_-12px_rgba(6,78,59,0.25)] px-3.5 py-2.5 text-sm">
      <div className="text-xs text-[#4B5563] mb-1">{label}</div>
      {(rows || payload).map((r, i) => {
        const p = payload.find((x) => x.dataKey === r.key) || payload[i];
        if (!p) return null;
        return (
          <div key={r.key || i} className="flex items-center gap-2">
            <span className="w-2.5 h-0.5 rounded-full" style={{ background: r.color || p.color }} />
            <span className="font-semibold text-[#1F2937] tabular-nums">{p.value?.toLocaleString?.() ?? p.value}</span>
            <span className="text-[#4B5563]">{r.label || p.name}</span>
          </div>
        );
      })}
    </div>
  );
}

// ---------------------------------------------------------------------
// Row 2 — User Growth: new users per bucket (the headline chart) plus a
// smaller cumulative-total area chart alongside it. Two separate charts,
// each on its own single axis — new_users (tens) and cumulative_users
// (hundreds, monotonically climbing to the lifetime total) are different
// orders of magnitude, so one shared axis would flatten the bars.
// ---------------------------------------------------------------------
function GrowthSection({ series, loading }) {
  const growth = series?.growth || [];
  const totalNew = growth.reduce((s, g) => s + g.new_users, 0);
  return (
    <div className="mt-6 grid lg:grid-cols-[1fr_360px] gap-4">
      <ChartCard title="User Growth" subtitle={`${totalNew.toLocaleString()} new users in this period`} loading={loading} testId="chart-user-growth">
        <ResponsiveContainer width="100%" height={280}>
          <BarChart data={growth} margin={{ top: 8, right: 8, left: 0, bottom: 0 }} barCategoryGap="30%">
            <CartesianGrid vertical={false} stroke={GRID} />
            <XAxis dataKey="label" tick={{ fontSize: 11, fill: SOFT }} axisLine={{ stroke: GRID }} tickLine={false}
              interval="preserveStartEnd" />
            <YAxis tick={{ fontSize: 11, fill: SOFT }} axisLine={false} tickLine={false} allowDecimals={false} width={32} />
            <Tooltip cursor={{ fill: "rgba(6,78,59,0.06)" }}
              content={<ChartTooltip rows={[{ key: "new_users", label: "New users", color: EMERALD }]} />} />
            <Bar dataKey="new_users" fill={EMERALD} radius={[4, 4, 0, 0]} maxBarSize={24} isAnimationActive={false} />
          </BarChart>
        </ResponsiveContainer>
      </ChartCard>

      <ChartCard title="Cumulative users" subtitle="Total registered, running total" loading={loading} testId="chart-cumulative-users">
        <ResponsiveContainer width="100%" height={280}>
          <AreaChart data={growth} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
            <defs>
              <linearGradient id="cumulativeFill" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={GOLD} stopOpacity={0.12} />
                <stop offset="100%" stopColor={GOLD} stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid vertical={false} stroke={GRID} />
            <XAxis dataKey="label" tick={{ fontSize: 10, fill: SOFT }} axisLine={{ stroke: GRID }} tickLine={false}
              interval="preserveStartEnd" />
            <YAxis tick={{ fontSize: 11, fill: SOFT }} axisLine={false} tickLine={false} allowDecimals={false}
              domain={["dataMin", "dataMax"]} width={40} />
            <Tooltip content={<ChartTooltip rows={[{ key: "cumulative_users", label: "Total users", color: GOLD }]} />} />
            <Area type="monotone" dataKey="cumulative_users" stroke={GOLD} strokeWidth={2} fill="url(#cumulativeFill)" isAnimationActive={false} />
          </AreaChart>
        </ResponsiveContainer>
      </ChartCard>
    </div>
  );
}

// ---------------------------------------------------------------------
// Row 3 — Learning / Platform Activity: Active Users on its own (a count
// of people), Attempts vs Completions grouped on its own (a count of
// events, and completions is always <= attempts — an ordinal funnel, light
// gold vs dark gold, same hue).
// ---------------------------------------------------------------------
function ActivitySection({ series, loading }) {
  const activity = series?.activity || [];
  return (
    <div className="mt-4 grid lg:grid-cols-2 gap-4">
      <ChartCard title="Active users" subtitle="Distinct learners active per bucket" loading={loading} testId="chart-active-users">
        <ResponsiveContainer width="100%" height={240}>
          <BarChart data={activity} margin={{ top: 8, right: 8, left: 0, bottom: 0 }} barCategoryGap="30%">
            <CartesianGrid vertical={false} stroke={GRID} />
            <XAxis dataKey="label" tick={{ fontSize: 11, fill: SOFT }} axisLine={{ stroke: GRID }} tickLine={false}
              interval="preserveStartEnd" />
            <YAxis tick={{ fontSize: 11, fill: SOFT }} axisLine={false} tickLine={false} allowDecimals={false} width={32} />
            <Tooltip cursor={{ fill: "rgba(6,78,59,0.06)" }}
              content={<ChartTooltip rows={[{ key: "active_users", label: "Active users", color: EMERALD }]} />} />
            <Bar dataKey="active_users" fill={EMERALD} radius={[4, 4, 0, 0]} maxBarSize={24} isAnimationActive={false} />
          </BarChart>
        </ResponsiveContainer>
      </ChartCard>

      <ChartCard title="Module attempts vs completions" subtitle="Self-assessment attempts, and how many passed" loading={loading} testId="chart-attempts-completions">
        <div className="flex items-center gap-4 text-xs text-[#4B5563] mb-1 px-1">
          <span className="inline-flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-sm" style={{ background: GOLD_LIGHT }} /> Attempts</span>
          <span className="inline-flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-sm" style={{ background: GOLD }} /> Completions</span>
        </div>
        <ResponsiveContainer width="100%" height={222}>
          <BarChart data={activity} margin={{ top: 4, right: 8, left: 0, bottom: 0 }} barCategoryGap="30%" barGap={2}>
            <CartesianGrid vertical={false} stroke={GRID} />
            <XAxis dataKey="label" tick={{ fontSize: 11, fill: SOFT }} axisLine={{ stroke: GRID }} tickLine={false}
              interval="preserveStartEnd" />
            <YAxis tick={{ fontSize: 11, fill: SOFT }} axisLine={false} tickLine={false} allowDecimals={false} width={32} />
            <Tooltip cursor={{ fill: "rgba(217,119,6,0.06)" }}
              content={<ChartTooltip rows={[
                { key: "module_attempts", label: "Attempts", color: GOLD_LIGHT },
                { key: "module_completions", label: "Completions", color: GOLD },
              ]} />} />
            <Bar dataKey="module_attempts" fill={GOLD_LIGHT} radius={[4, 4, 0, 0]} maxBarSize={16} isAnimationActive={false} />
            <Bar dataKey="module_completions" fill={GOLD} radius={[4, 4, 0, 0]} maxBarSize={16} isAnimationActive={false} />
          </BarChart>
        </ResponsiveContainer>
      </ChartCard>
    </div>
  );
}

function ChartCard({ title, subtitle, loading, testId, children }) {
  return (
    <div className="rounded-2xl bg-white border border-[#E7E0D2] p-5" data-testid={testId}>
      <div className="flex items-baseline justify-between gap-2">
        <h3 className="font-heading text-base font-semibold text-[#1F2937]">{title}</h3>
      </div>
      {subtitle && <p className="text-xs text-[#9CA3AF] mt-0.5">{subtitle}</p>}
      <div className={`mt-2 transition-opacity ${loading ? "opacity-50" : ""}`}>{children}</div>
    </div>
  );
}

// ---------------------------------------------------------------------
// Row 4 — Module Performance: existing table, now with a meter bar.
// Meter fill = darkest ramp step (brand emerald); unfilled track = the
// app's own border token, a lighter step of the same neutral family.
// ---------------------------------------------------------------------
// memo: neither Module Performance nor Demographics depend on the period
// selector's state (period/offset/series) — without this they'd re-render,
// and recharts would re-run its mount/enter animation, on every period
// click even though their own data never changed. Caught via screenshot:
// a period click landing mid-reanimation showed the age-distribution bars
// missing (still at pre-animation height), not an actual data bug.
const ModulePerformance = memo(function ModulePerformance({ moduleStats }) {
  return (
    <div className="mt-6 rounded-2xl bg-white border border-[#E7E0D2] overflow-hidden" data-testid="module-performance">
      <div className="px-5 py-3 border-b border-[#E7E0D2] text-xs uppercase tracking-widest text-[#4B5563]">Module performance</div>
      <table className="w-full text-sm">
        <thead className="bg-[#FDFBF7] text-[#4B5563] text-left">
          <tr>
            <th className="px-4 py-3 font-medium">Module</th>
            <th className="px-4 py-3 font-medium">Attempts</th>
            <th className="px-4 py-3 font-medium">Passed</th>
            <th className="px-4 py-3 font-medium">Pass rate</th>
          </tr>
        </thead>
        <tbody>
          {(moduleStats || []).map((m) => (
            <tr key={m.code} className="border-t border-[#E7E0D2]">
              <td className="px-4 py-3 font-medium text-[#1F2937] whitespace-nowrap">{m.name}</td>
              <td className="px-4 py-3 text-[#4B5563] tabular-nums">{m.attempts}</td>
              <td className="px-4 py-3 text-[#4B5563] tabular-nums">{m.passed}</td>
              <td className="px-4 py-3">
                <div className="flex items-center gap-3 max-w-[220px]">
                  <div className="flex-1 h-2 rounded-full overflow-hidden" style={{ background: EMERALD_TRACK }}>
                    <div className="h-full rounded-full" style={{ width: `${m.pass_rate}%`, background: EMERALD }} />
                  </div>
                  <span className="font-semibold text-[#064E3B] tabular-nums text-xs w-9 text-right">{m.pass_rate}%</span>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
});

// ---------------------------------------------------------------------
// Row 5 — Demographics. Age is ordinal (fixed band order carries meaning)
// so it takes the 6-step emerald ramp, lightest = youngest. Gender,
// Country and Language are nominal — each rendered as a ranked
// single-hue bar list (one implicit series; each bar's own label already
// carries identity, per the "one series, no legend" rule), sorted by
// count. Country/Language cap at the top 6 + an "Other" bucket so a long
// tail doesn't blow out the card.
// ---------------------------------------------------------------------
function countBy(users, field, order) {
  const counts = {};
  for (const u of users) {
    const v = (u[field] || "").trim() || "Not specified";
    counts[v] = (counts[v] || 0) + 1;
  }
  const keys = order
    ? [...order.filter((k) => counts[k]), ...Object.keys(counts).filter((k) => !order.includes(k))]
    : Object.keys(counts).sort((a, b) => counts[b] - counts[a]);
  return keys.map((k) => ({ label: k, count: counts[k] }));
}

function topNPlusOther(rows, n) {
  if (rows.length <= n) return rows;
  const top = rows.slice(0, n);
  const rest = rows.slice(n).reduce((s, r) => s + r.count, 0);
  return [...top, { label: "Other", count: rest }];
}

const DemographicBreakdown = memo(function DemographicBreakdown({ data }) {
  // Hooks always run, in the same order, on every render — the "any users
  // yet?" bail-out has to come *after* them, not before. (countBy/age.map
  // handle an empty `users` array fine on their own; the early return below
  // is purely to skip rendering the section, not something the hooks need.)
  // `data.users || []` is memoized too — a bare fallback would hand every
  // downstream useMemo below a fresh [] reference (and a new dependency)
  // on every render whenever data.users is ever falsy.
  const users = useMemo(() => data.users || [], [data.users]);

  const age = useMemo(() => {
    const rows = countBy(users, "age_group", AGE_GROUPS_ORDER);
    // Keep every defined band even at zero, so the ramp's order stays intact.
    return AGE_GROUPS_ORDER.map((label, i) => ({
      label, count: rows.find((r) => r.label === label)?.count || 0, fill: EMERALD_RAMP[i],
    }));
  }, [users]);

  const gender = useMemo(() => countBy(users, "gender"), [users]);
  const country = useMemo(() => topNPlusOther(countBy(users, "country"), 6), [users]);
  const language = useMemo(() => topNPlusOther(countBy(users, "language_preference"), 6), [users]);

  if (!users.length) return null;

  return (
    <div className="mt-6" data-testid="demographic-breakdown">
      <h2 className="font-heading text-xl font-semibold text-[#1F2937]">Demographics</h2>
      <div className="mt-3 grid lg:grid-cols-2 gap-4">
        <ChartCard title="Age distribution" testId="demographic-age_group">
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={age} margin={{ top: 8, right: 8, left: 0, bottom: 0 }} barCategoryGap="25%">
              <CartesianGrid vertical={false} stroke={GRID} />
              <XAxis dataKey="label" tick={{ fontSize: 11, fill: SOFT }} axisLine={{ stroke: GRID }} tickLine={false} />
              <YAxis tick={{ fontSize: 11, fill: SOFT }} axisLine={false} tickLine={false} allowDecimals={false} width={32} />
              <Tooltip cursor={{ fill: "rgba(6,78,59,0.06)" }}
                content={<ChartTooltip rows={[{ key: "count", label: "Users", color: EMERALD }]} />} />
              <Bar dataKey="count" radius={[4, 4, 0, 0]} maxBarSize={40} isAnimationActive={false}>
                {age.map((row) => <RampCell key={row.label} fill={row.fill} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>

        <RankedList title="Gender" rows={gender} testId="demographic-gender" />
        <RankedList title="Top countries" rows={country} testId="demographic-country" />
        <RankedList title="Languages" rows={language} testId="demographic-language" />
      </div>
    </div>
  );
});

// recharts needs each Bar segment as an explicit <Cell> to vary fill by
// data index (an ordinal ramp, not one flat series color).
function RampCell({ fill }) {
  return <Cell fill={fill} />;
}

function RankedList({ title, rows, testId }) {
  const max = Math.max(1, ...rows.map((r) => r.count));
  return (
    <ChartCard title={title} testId={testId}>
      <div className="flex flex-col gap-2.5 py-1">
        {rows.map((r) => (
          <div key={r.label} className="flex items-center gap-3">
            <span className="text-sm text-[#1F2937] w-28 shrink-0 truncate">{r.label}</span>
            <div className="flex-1 h-2 rounded-full overflow-hidden" style={{ background: EMERALD_TRACK }}>
              <div className="h-full rounded-full" style={{ width: `${(r.count / max) * 100}%`, background: EMERALD }} />
            </div>
            <span className="text-sm font-semibold text-[#064E3B] tabular-nums w-10 text-right">{r.count}</span>
          </div>
        ))}
        {!rows.length && <div className="text-sm text-[#9CA3AF]">No data yet.</div>}
      </div>
    </ChartCard>
  );
}

// ---------------------------------------------------------------------
// CSV exports — unchanged from the previous version.
// ---------------------------------------------------------------------
function downloadCsv(filename, rows) {
  if (!rows.length) { toast.error("Nothing to export"); return; }
  const headers = Object.keys(rows[0]);
  const escape = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
  const csv = [headers.join(","), ...rows.map((r) => headers.map((h) => escape(r[h])).join(","))].join("\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = filename;
  document.body.appendChild(a); a.click(); a.remove();
  URL.revokeObjectURL(url);
}

function ReportsPanel({ data }) {
  const users = data.users || [];

  const exportUserProgress = () => {
    downloadCsv("user-progress.csv", users.map((u) => ({
      name: u.name, email: u.email, country: u.country || "", language: u.language_preference || "",
      age_group: u.age_group || "", gender: u.gender || "",
      country_calling_code: u.country_calling_code || "", mobile_number: u.mobile_number || "",
      modules_passed: u.modules_passed ?? "", certificate_issued: u.certificate_issued ? "yes" : "no",
    })));
  };

  const exportModuleProgress = () => {
    downloadCsv("module-progress.csv", (data.module_stats || []).map((m) => ({
      module: m.name, code: m.code, attempts: m.attempts, passed: m.passed, pass_rate_pct: m.pass_rate,
    })));
  };

  const exportAnalytics = () => {
    const dims = ["language_preference", "country", "country_calling_code", "age_group", "gender"];
    const rows = [];
    for (const dim of dims) {
      for (const row of countBy(users, dim)) {
        rows.push({ dimension: dim, value: row.label, count: row.count });
      }
    }
    for (const f of (data.feedback_summary || [])) {
      rows.push({ dimension: "module_feedback", value: `${f.module_code} avg_rating=${f.avg_rating}`, count: f.count });
    }
    downloadCsv("analytics.csv", rows);
  };

  return (
    <div className="mt-8 rounded-2xl bg-white border border-[#E7E0D2] p-5" data-testid="reports-panel">
      <div className="text-xs uppercase tracking-widest text-[#4B5563]">Downloadable reports</div>
      <div className="mt-3 flex flex-wrap gap-3">
        <button data-testid="export-user-progress" onClick={exportUserProgress}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white border border-[#E7E0D2] hover:border-[#064E3B] text-sm text-[#1F2937] transition">
          <Download className="w-4 h-4" /> User Progress
        </button>
        <button data-testid="export-module-progress" onClick={exportModuleProgress}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white border border-[#E7E0D2] hover:border-[#064E3B] text-sm text-[#1F2937] transition">
          <Download className="w-4 h-4" /> Module Progress
        </button>
        <button data-testid="export-analytics" onClick={exportAnalytics}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white border border-[#E7E0D2] hover:border-[#064E3B] text-sm text-[#1F2937] transition">
          <Download className="w-4 h-4" /> Analytics
        </button>
      </div>
    </div>
  );
}
